import { useEffect, useState } from 'react';

import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { flexRender } from '@tanstack/react-table';
import {
  AlertTriangleIcon,
  ExternalLinkIcon,
  PrinterIcon,
  RefreshCwIcon,
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useSnapshot } from 'valtio';

import { DialogDeleteRecordConfirmation } from '../../../components/pages/backoffice/dialogs/delete-record.jsx';
import { DialogVoidInvoiceConfirmation } from '../../../components/pages/backoffice/dialogs/invoice-void.jsx';
import CustomerCoupons from '../../../components/pages/backoffice/printables/customer-coupons-receipt.jsx';
import DecorativeInput from '../../../components/decorative-input.jsx';
import Error from '../../../components/feedback/error.jsx';
import Loading from '../../../components/feedback/loading.jsx';
import ImageInspector from '../../../components/image-inspector.jsx';
import { DataTableColumnHeader } from '../../../components/table/data-table-column-header.jsx';
import { DataTableFooter } from '../../../components/table/data-table-footer.jsx';
import Heading from '../../../components/typography/heading.jsx';
import { Badge } from '../../../components/ui/badge.jsx';
import { Button } from '../../../components/ui/button.jsx';
import FormCard from '../../../components/ui/form-card.jsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table.jsx';
import { Textarea } from '../../../components/ui/textarea.jsx';
import { msCatalystApi } from '../../../services/api/ms-catalyst-api.js';
import { authState } from '../../../services/state/auth-state.jsx';
import { print } from '../../../helpers/print.js';
import { useTable } from '../../../hooks/use-table.jsx';
import {
  CATALYST_BACKOFFICEUSER_ROLES,
  COUPONS_STATES,
  INVOICES_STATES,
} from '../../../utils/constants.js';

const COUPONS_TABLE_COLUMNS = [
  {
    title: 'Id',
    accessorKey: 'id',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Id" />
    ),
    cell: ({ row }) => <div>{row.getValue('id')}</div>,
    enableSorting: false,
    enableHiding: false,
  },
  {
    title: 'Estado',
    accessorKey: 'status',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Estado" />
    ),
    cell: ({ row }) => {
      const couponStatus = row.getValue('status');
      const badgeVariant =
        couponStatus === COUPONS_STATES.VALID ? 'success' : 'outline';

      return (
        <Badge className="min-w-24 justify-center" variant={badgeVariant}>
          {couponStatus}
        </Badge>
      );
    },
  },
  {
    title: 'Data de Criação',
    accessorKey: 'createdTime',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data de Criação" />
    ),
    cell: ({ row }) => <div>{row.getValue('createdTime').slice(0, -4)}</div>,
    enableSorting: true,
    enableHiding: true,
  },
];

export function InvoicesShow() {
  const { invoiceId } = useParams();
  const { user, isAdmin } = useSnapshot(authState);
  const isOperatorAdmin =
    user?.details?.role === CATALYST_BACKOFFICEUSER_ROLES.user_admin;

  const [printCoupons, setPrintCoupons] = useState(false);

  const [proofFileType, setProofFileType] = useState('');

  const {
    table,
    query: couponsQuery,
    filters,
  } = useTable(
    COUPONS_TABLE_COLUMNS,
    {
      queryKey: ['invoice_coupons'],
      queryFn: async () => {
        const { data: invoiceCoupons } = await msCatalystApi.get(
          `coupons/paginate?${filters}&invoices.ROWID=${invoiceId}`,
        );
        return invoiceCoupons.data;
      },
      placeholderData: keepPreviousData,
      select: (data) => {
        return {
          ...data,
          rows: data.rows.map((item) => ({
            createdTime: item.coupons.CREATEDTIME,
            id: item.coupons.ROWID,
            status: item.coupons.status,
            invoice: item.invoices,
            shop: item.shops,
          })),
        };
      },
    },
    {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
  );

  const queryInvoice = useQuery({
    queryKey: ['invoice'],
    queryFn: async () => {
      const { data: invoices } = await msCatalystApi.get(
        `/invoices/${invoiceId}`,
      );
      const invoicesData = invoices.data;

      if (invoicesData.invoices?.proofFilename) {
        const splitFilename = invoicesData.invoices.proofFilename.split('.');
        const fileExtension = splitFilename[splitFilename.length - 1];

        setProofFileType(fileExtension);
      }

      return {
        customer: invoicesData.customers,
        status: invoicesData.invoices.status,
        campaign: invoicesData.campaigns.name,
        campaignLegalPhrase: invoicesData.campaigns.legalPhrase,
        amount: invoicesData.invoices.amount,
        issuedDate: invoicesData.invoices.issuedDate,
        code: invoicesData.invoices.code,
        shopId: invoicesData.shops.ROWID,
        shop: invoicesData.shops.name,
        proofFileUrl: invoicesData.invoices.proofFileUrl,
        reason: invoicesData.invoices.reason,
      };
    },
  });

  const queryInvoiceAllCoupons = useQuery({
    queryKey: ['invoice_all_coupons'],
    queryFn: async () => {
      const { data: allCoupons } = await msCatalystApi.get(
        `coupons/all?invoices.ROWID=${invoiceId}`,
      );

      return allCoupons.data;
    },
    select: (data) => {
      return data.map((item) => ({
        ...item.coupons,
        id: item.ROWID,
      }));
    },
    enabled: printCoupons,
  });

  const mutationDeleteInvoice = useMutation({
    mutationFn: async () => {
      await msCatalystApi.delete(`/invoices/${invoiceId}`);
    },
  });

  const mutationVoidInvoice = useMutation({
    mutationFn: async (text) => {
      await msCatalystApi.put(`invoices/void/${invoiceId}`, {
        reason: text,
      });
    },
  });

  useEffect(() => {
    if (printCoupons && queryInvoiceAllCoupons.isSuccess) {
      print({
        component: (
          <CustomerCoupons
            user={user}
            customer={queryInvoice.data?.customer}
            coupons={queryInvoiceAllCoupons.data}
            campaign={{
              name: queryInvoice.data?.campaign,
              legalPhrase: queryInvoice.data?.campaignLegalPhrase,
            }}
          />
        ),
      });

      setPrintCoupons(!printCoupons);
    }
  }, [queryInvoiceAllCoupons.isSuccess, printCoupons]);

  return (
    <>
      <div className="flex flex-col items-start justify-start gap-y-6 pb-6 pt-4 lg:flex-row lg:items-center lg:justify-between lg:gap-y-0">
        <div>
          <Heading className="pb-2">Fatura</Heading>
          {!queryInvoice.isLoading &&
          !queryInvoice.isError &&
          !queryInvoice.isRefetching &&
          !queryInvoice.isRefetchError ? (
            <>
              {queryInvoice.data.status === INVOICES_STATES.PENDING ? (
                <Badge
                  className="bg-amber-500 hover:bg-amber-500 dark:text-stone-800"
                  variant="secondary"
                >
                  {queryInvoice.data.status}
                </Badge>
              ) : queryInvoice.data.status === INVOICES_STATES.APPROVED ? (
                <Badge className="min-w-24 justify-center" variant="success">
                  {queryInvoice.data.status}
                </Badge>
              ) : queryInvoice.data.status === INVOICES_STATES.REJECTED ||
                queryInvoice.data.status === INVOICES_STATES.VOIDED ? (
                <Badge
                  className="min-w-24 justify-center"
                  variant="destructive"
                >
                  {queryInvoice.data.status}
                </Badge>
              ) : (
                <Badge className="min-w-24 justify-center" variant="outline">
                  {queryInvoice.data.status}
                </Badge>
              )}
            </>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2.5">
          {!queryInvoice.isLoading &&
            !queryInvoice.isError &&
            !queryInvoice.isRefetching &&
            !queryInvoice.isRefetchError && (
              <>
                <Button asChild>
                  <a
                    href={queryInvoice.data.proofFileUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <ExternalLinkIcon />
                    Comprovativo
                  </a>
                </Button>

                {queryInvoice?.data?.status === INVOICES_STATES.APPROVED && (
                  <Button
                    onClick={() => {
                      if (queryInvoiceAllCoupons?.isSuccess) {
                        print({
                          component: (
                            <CustomerCoupons
                              user={user}
                              customer={queryInvoice.data?.customer}
                              coupons={queryInvoiceAllCoupons.data}
                              campaign={{
                                name: queryInvoice.data?.campaign,
                                legalPhrase:
                                  queryInvoice.data?.campaignLegalPhrase,
                              }}
                            />
                          ),
                        });
                      } else {
                        setPrintCoupons(true);
                      }
                    }}
                  >
                    <PrinterIcon />
                    Imprimir Cupões
                  </Button>
                )}

                {(isAdmin || isOperatorAdmin) &&
                queryInvoice?.data?.status !== INVOICES_STATES.VOIDED ? (
                  <DialogVoidInvoiceConfirmation
                    recordName={queryInvoice?.data?.code}
                    redirect="/backoffice/invoices"
                    action={async (text) =>
                      mutationVoidInvoice.mutateAsync(text)
                    }
                  />
                ) : null}

                {isAdmin &&
                queryInvoice?.data?.status !== INVOICES_STATES.REJECTED ? (
                  <DialogDeleteRecordConfirmation
                    recordName={queryInvoice?.data?.code}
                    redirect="/backoffice/invoices"
                    action={async () => mutationDeleteInvoice.mutateAsync()}
                  />
                ) : null}
              </>
            )}
        </div>
      </div>

      {queryInvoice.isLoading || queryInvoice.isRefetching ? (
        <Loading />
      ) : queryInvoice.isError || queryInvoice.isRefetchError ? (
        <Error />
      ) : (
        <div className="flex flex-col space-y-10">
          {/* Invoice */}
          <div className="flex flex-col gap-5">
            <FormCard>
              <DecorativeInput
                value={queryInvoice.data?.campaign}
                label="Campanha"
              />
              <DecorativeInput
                value={queryInvoice.data?.customer?.fullName}
                label="Cliente"
              />
              <DecorativeInput
                value={queryInvoice.data?.code}
                label="Identificador da Fatura"
              />
              <DecorativeInput
                value={queryInvoice.data?.amount}
                label="Valor da Fatura"
              />
              <DecorativeInput
                value={queryInvoice.data?.issuedDate}
                label="Data de Emissão da Fatura"
              />
              <DecorativeInput value={queryInvoice.data?.shop} label="Loja" />

              {queryInvoice.data.status === INVOICES_STATES.VOIDED ||
              queryInvoice.data.status === INVOICES_STATES.REJECTED ? (
                <div className="col-span-1 lg:col-span-2">
                  <p className="mb-3 inline-block text-sm font-medium leading-none">
                    {queryInvoice.data.status === INVOICES_STATES.VOIDED
                      ? 'Motivo de Anulação'
                      : 'Motivo de Rejeição'}
                  </p>
                  <Textarea readOnly={true} value={queryInvoice.data.reason} />
                </div>
              ) : null}
            </FormCard>
          </div>

          {/* Coupons */}
          {queryInvoice.data.status === INVOICES_STATES.APPROVED ? (
            <div className="flex flex-col gap-5">
              <div>
                <h2 className="text-xl font-medium">Cupões</h2>
                {couponsQuery.data?.isLoading ||
                couponsQuery.data?.isRefetching ? (
                  <div className="flex flex-col justify-center pb-1 pt-6">
                    <Loading />
                  </div>
                ) : !couponsQuery.data?.rowCount ? (
                  <div className="flex flex-col justify-center gap-y-5 pb-1">
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                      Por favor aguarde enquanto os cupões ainda estão a ser
                      gerados.
                    </p>
                    <p>
                      Carregue no botão abaixo ou recarregue a página para
                      verificar se os cupões foram gerados
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        couponsQuery.refetch();
                      }}
                    >
                      <RefreshCwIcon />
                      Recarregar
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-y-5 pt-1">
                    <h3 className="text-sm text-zinc-600 dark:text-zinc-400">
                      Foram gerados {couponsQuery.data?.rowCount || 0} cupões
                      para esta fatura
                    </h3>

                    <div className="shadow-sm md:rounded-lg md:border md:border-muted-foreground/35 md:bg-muted/40">
                      <Table>
                        <TableHeader>
                          {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                              {headerGroup.headers.map((header) => {
                                return (
                                  <TableHead
                                    key={header.id}
                                    colSpan={header.colSpan}
                                  >
                                    {header.isPlaceholder
                                      ? null
                                      : flexRender(
                                          header.column.columnDef.header,
                                          header.getContext(),
                                        )}
                                  </TableHead>
                                );
                              })}
                            </TableRow>
                          ))}
                        </TableHeader>

                        <TableBody>
                          {couponsQuery.isRefetching ||
                          couponsQuery.isPending ? (
                            <TableRow>
                              <TableCell
                                colSpan={COUPONS_TABLE_COLUMNS.length}
                                className="h-24 text-center"
                              >
                                <Loading />
                              </TableCell>
                            </TableRow>
                          ) : couponsQuery.isRefetchError ? (
                            <TableRow>
                              <TableCell
                                colSpan={COUPONS_TABLE_COLUMNS.length}
                                className="h-24 text-center"
                              >
                                <Error />
                              </TableCell>
                            </TableRow>
                          ) : table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                              <TableRow
                                key={row.id}
                                data-state={row.getIsSelected() && 'selected'}
                              >
                                {row.getVisibleCells().map((cell) => (
                                  <TableCell key={cell.id}>
                                    {flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext(),
                                    )}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell
                                colSpan={COUPONS_TABLE_COLUMNS.length}
                                className="h-24 text-center"
                              >
                                Não foram encontrados resultados.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>

                    <DataTableFooter
                      table={table}
                      pageSize={[10, 50, 100]}
                      hide={{ selectedRows: true, skipAll: true }}
                    />
                  </div>
                )}
              </div>
            </div>
          ) : null}

          <div className="flex flex-col gap-5">
            <h2 className="text-xl font-medium">Comprovativo</h2>

            {!queryInvoice.data.proofFileUrl ? (
              <h2 className="flex flex-row font-medium text-orange-600">
                <AlertTriangleIcon className="mr-2 size-5" /> Nenhum
                comprovativo da fatura foi submetido
              </h2>
            ) : proofFileType === 'pdf' ? (
              <a
                href={queryInvoice.data.proofFileUrl}
                target="_blank"
                rel="noreferrer noopener"
              >
                <Button>
                  <ExternalLinkIcon className="mr-2 size-4" />
                  Ver PDF
                </Button>
              </a>
            ) : (
              <ImageInspector src={queryInvoice.data.proofFileUrl} />
            )}
          </div>
        </div>
      )}
    </>
  );
}
